import { Video } from 'lucide-react';

export interface VideoEmbedProps {
  url: string | null;
}

/**
 * Turns a plain video url into an embeddable one. Provider detection lives here rather than in the
 * backend, so supporting another host never needs an API change.
 */
const toEmbedUrl = (url: string): string | null => {
  // Capture the id and, when present, the private-link hash from either the path (vimeo.com/id/hash)
  // or the query (?h=hash). Dropping the hash breaks embedding of unlisted / domain-private videos.
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/(\w+))?/);
  if (vimeo) {
    const id = vimeo[1];
    const hash = vimeo[2] ?? url.match(/[?&]h=(\w+)/)?.[1];
    return `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ''}`;
  }

  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`;

  return null;
};

const VideoEmbed = ({ url }: VideoEmbedProps) => {
  const embedUrl = url ? toEmbedUrl(url) : null;

  if (!url) return null;

  // An unknown host still gets a usable affordance instead of a blank frame.
  if (!embedUrl) {
    return (
      <div className="aspect-video rounded-xl bg-[#F0F4FC] border border-[#E4E7EC] flex flex-col items-center justify-center gap-2 text-[#888] px-4 text-center">
        <Video className="w-6 h-6" />
        <span className="text-[13px]">Vorschau nicht möglich</span>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-[12px] underline decoration-[#5FD5DE] underline-offset-2 break-all">
          {url}
        </a>
      </div>
    );
  }

  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-black">
      <iframe
        src={embedUrl}
        title="Lektionsvideo"
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; web-share"
        // Vimeo verifies domain-restricted videos via the Referer; matches the other Vimeo embeds in the app.
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </div>
  );
};

export default VideoEmbed;
